/**
 * SituatieLucrariEditor - ANEXA 2: Situație de Lucrări
 * Layout identic cu devizul dar cu tonuri de albastru
 * Articolele se preiau din devizul atașat contractului
 */
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save, ClipboardList, Plus, Trash2, Info, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateSituatieHTML(d, items, totals, h) {
  const rows = items.map((item, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${item.name || ""}</td>
      <td style="text-align:center">${item.um || "buc"}</td>
      <td style="text-align:right">${(item.quantity || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 })}</td>
      <td style="text-align:right">${(item.price || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 })}</td>
      <td style="text-align:right;font-weight:600">${((item.quantity || 0) * (item.price || 0)).toLocaleString("ro-RO", { minimumFractionDigits: 2 })}</td>
    </tr>`).join("");

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
    body{font-family:'Segoe UI',Arial,sans-serif;max-width:900px;margin:30px auto;padding:20px;color:#1e293b;font-size:12px;line-height:1.5}
    h1{text-align:center;color:#1d4ed8;font-size:18px;margin-bottom:4px}
    h2{text-align:center;color:#2563eb;font-size:13px;font-weight:normal;margin-top:0}
    .header{border-bottom:3px solid #2563eb;padding-bottom:12px;margin-bottom:15px}
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:15px}
    .info-box{padding:8px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px}
    .info-box label{font-size:10px;color:#3b82f6;text-transform:uppercase;letter-spacing:0.5px}
    .info-box p{margin:2px 0 0;font-weight:600;color:#1e3a5f}
    table{width:100%;border-collapse:collapse;margin:12px 0}
    th{background:#1d4ed8;color:white;padding:8px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.3px}
    td{padding:6px 8px;border:1px solid #dbeafe}
    tr:nth-child(even){background:#f0f7ff}
    .totals{margin-top:12px;text-align:right}
    .totals table{width:auto;margin-left:auto}
    .totals td{padding:6px 16px;border:1px solid #93c5fd}
    .totals td:first-child{background:#eff6ff;font-weight:600;color:#1d4ed8}
    .totals td:last-child{font-weight:700;font-size:13px}
    .signatures{display:flex;justify-content:space-between;margin-top:40px;page-break-inside:avoid}
    .sig-box{width:45%;text-align:center;border-top:2px solid #2563eb;padding-top:10px}
    .sig-box p{margin:4px 0;font-size:11px}
    .footer{text-align:center;margin-top:25px;font-size:10px;color:#64748b;border-top:1px solid #e2e8f0;padding-top:8px}
  </style></head><body>
  <div class="header">
    <h1>SITUAȚIE DE LUCRĂRI NR. ${d.situatie_number || "1"} DIN ${d.situatie_date || new Date().toLocaleDateString("ro-RO")}</h1>
    <h2>${(h && h.annex_label) || "ANEXA"} CTR ${d.contract_number || "___"} — Situație de Lucrări Nr. ${d.situatie_number || "1"} (Contract nr. ${d.contract_number || "___"} din ${d.contract_date || "___"})</h2>
  </div>
  <div class="info-grid">
    <div class="info-box"><label>Executant</label><p>${d.executant_name || "___"}</p></div>
    <div class="info-box"><label>Beneficiar</label><p>${d.beneficiar_name || "___"}</p></div>
    <div class="info-box"><label>Obiect lucrare</label><p>${d.obiect_lucrare || "___"}</p></div>
    <div class="info-box"><label>Locație</label><p>${d.locatie_lucrare || "___"}</p></div>
  </div>
  <table>
    <thead><tr><th style="width:40px">Nr.</th><th>Descriere lucrare</th><th style="width:50px">UM</th><th style="width:70px">Cantitate</th><th style="width:80px">Preț unitar</th><th style="width:90px">Valoare</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="totals"><table>
    <tr><td>Total fără TVA</td><td>${totals.totalFaraTVA.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei</td></tr>
    <tr><td>TVA (${d.tva_percent || 21}%)</td><td>${totals.tva.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei</td></tr>
    <tr><td>TOTAL CU TVA</td><td style="color:#1d4ed8;font-size:14px">${totals.totalCuTVA.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei</td></tr>
  </table></div>
  <div class="signatures">
    <div class="sig-box"><p><strong>EXECUTANT</strong></p><p>${d.executant_name || "___"}</p><p>${d.executant_reprezentant || "___"}</p><p style="margin-top:25px">(semnătura)</p></div>
    <div class="sig-box"><p><strong>BENEFICIAR</strong></p><p>${d.beneficiar_name || "___"}</p><p>${d.beneficiar_reprezentant || "___"}</p><p style="margin-top:25px">(semnătura)</p></div>
  </div>
  <div class="footer">Document generat de <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a> — ${new Date().toLocaleDateString("ro-RO")}</div>
  </body></html>`;
}

export default function SituatieLucrariEditor({ contractData, devizData, existingAnnex, onSave, onCancel, annexCount, companyId }) {
  const existing = existingAnnex?.data || {};
  const situatieNum = existing.situatie_number || (annexCount || 0) + 1;

  // ----- Calculează factorul de markup din deviz -----
  // Prețul unitar din SL trebuie să reflecte totalFaraTVA din deviz
  // (adică include contribuție muncă 2.25% + indirecte 4% + profit 5%).
  const markupFactor = useMemo(() => {
    const totals = devizData?.totals || {};
    const totalFaraTVA = parseFloat(totals.totalFaraTVA || 0);
    const totalDirect = parseFloat(totals.totalDirect || 0);
    if (totalDirect > 0 && totalFaraTVA > 0) return totalFaraTVA / totalDirect;
    // Fallback: compute from percentages if available
    const p = devizData?.percentages || {};
    const cm = 1 + parseFloat(p.contributieMunca || 0) / 100;
    const ci = 1 + parseFloat(p.cheltuieliIndirecte || 0) / 100;
    const pr = 1 + parseFloat(p.profit || 0) / 100;
    // Approx factor (assumes 50/50 material/manopera - decent default)
    return ((1 * ci * pr) + (cm * ci * pr)) / 2;
  }, [devizData]);

  // ----- Fetch SL-uri anterioare pentru cantități rămase -----
  const [previousQtyMap, setPreviousQtyMap] = useState(null); // itemName -> qty already invoiced
  useEffect(() => {
    if (!contractData?.contract_id && !existingAnnex) return;
    const contractId = contractData?.contract_id || existingAnnex?.contract_id;
    if (!contractId) return;
    (async () => {
      try {
        const params = companyId ? { company_id: companyId } : {};
        const { data } = await axios.get(`${API}/contracts/${contractId}/annexes`, { params });
        const previousSLs = (data || []).filter(a =>
          a.annex_type === "situatie_lucrari" && a.id !== existingAnnex?.id
        );
        const acc = {};
        previousSLs.forEach(sl => {
          const slItems = sl.data?.items || [];
          slItems.forEach(it => {
            const key = (it.name || "").trim().toLowerCase();
            if (!key) return;
            acc[key] = (acc[key] || 0) + parseFloat(it.quantity || 0);
          });
        });
        setPreviousQtyMap(acc);
      } catch (e) {
        console.error("Eroare fetch SL-uri anterioare:", e);
        setPreviousQtyMap({});
      }
    })();
  }, [contractData?.contract_id, existingAnnex?.id, companyId]);

  // ----- Inițializare items -----
  // Prima emitere: cantitatea rămasă din deviz (= total_deviz - cantități facturate anterior).
  // Editare SL existentă: folosește items salvați.
  const initialItems = useMemo(() => {
    if (existing.items && existing.items.length > 0) return existing.items;
    if (!devizData?.workItems) return [{ name: "", um: "buc", quantity: 0, price: 0, deviz_quantity: 0 }];
    // previousQtyMap not loaded yet -> use full quantities (first render)
    const prevMap = previousQtyMap || {};
    return devizData.workItems.map((wi) => {
      const key = (wi.name || wi.description || "").trim().toLowerCase();
      const devizQty = parseFloat(wi.quantity || 0);
      const alreadyInvoiced = parseFloat(prevMap[key] || 0);
      const remaining = Math.max(0, devizQty - alreadyInvoiced);
      const directUnit = parseFloat(wi.materialPrice || 0) + parseFloat(wi.manoperaPrice || 0);
      const finalUnit = directUnit * markupFactor;
      return {
        name: wi.name || wi.description || "",
        um: wi.um || "buc",
        quantity: remaining,           // default: cantitatea rămasă
        price: Math.round(finalUnit * 100) / 100,
        deviz_quantity: devizQty,      // referință: cantitatea originală din deviz
        already_invoiced: alreadyInvoiced,
      };
    });
  }, [existing.items, devizData, markupFactor, previousQtyMap]);

  const [form, setForm] = useState({
    situatie_number: situatieNum,
    situatie_date: existing.situatie_date || new Date().toISOString().split("T")[0],
    contract_number: existing.contract_number || contractData.contract_number || "",
    contract_date: existing.contract_date || contractData.contract_date || "",
    executant_name: existing.executant_name || contractData.executant_name || "",
    executant_reprezentant: existing.executant_reprezentant || contractData.executant_reprezentant || "",
    beneficiar_name: existing.beneficiar_name || contractData.beneficiar_name || "",
    beneficiar_reprezentant: existing.beneficiar_reprezentant || contractData.beneficiar_reprezentant || "",
    obiect_lucrare: existing.obiect_lucrare || contractData.obiect_lucrare || "",
    locatie_lucrare: existing.locatie_lucrare || contractData.locatie_lucrare || "",
    tva_percent: existing.tva_percent || 21,
  });
  const [items, setItems] = useState(initialItems);

  // Re-set items când sosesc datele SL anterioare (schimbă cantitățile rămase)
  useEffect(() => {
    if (previousQtyMap === null) return;              // still loading
    if (existing.items && existing.items.length > 0) return; // editare, nu reseta
    setItems(initialItems);
  }, [previousQtyMap, existing.items, initialItems]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const updateItem = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    setItems(next);
  };

  const addItem = () => setItems([...items, { name: "", um: "buc", quantity: 0, price: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    const totalFaraTVA = items.reduce((s, it) => s + (it.quantity || 0) * (it.price || 0), 0);
    const tva = totalFaraTVA * (form.tva_percent / 100);
    return { totalFaraTVA, tva, totalCuTVA: totalFaraTVA + tva };
  }, [items, form.tva_percent]);

  const handleSave = () => {
    const dataPayload = { ...form, items };
    const header = { annex_label: existingAnnex?.annex_label || "ANEXA" };
    const html = generateSituatieHTML(form, items, totals, header);
    onSave({
      annex_type: "situatie_lucrari",
      data: dataPayload,
      html_content: html,
      title: `Situație de Lucrări Nr. ${form.situatie_number}`,
    });
  };

  return (
    <div className="space-y-4" data-testid="situatie-lucrari-editor">
      <Card className="border-0 shadow-lg border-t-4 border-t-blue-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel} className="p-1 h-auto"><ArrowLeft className="w-5 h-5" /></Button>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              {existingAnnex?.annex_label || "ANEXA"} — Situație de Lucrări Nr. {form.situatie_number}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label>Nr. situație</Label><Input type="number" value={form.situatie_number} onChange={(e) => upd("situatie_number", e.target.value)} className="border-blue-300 focus:border-blue-500" /></div>
            <div><Label>Data</Label><Input type="date" value={form.situatie_date} onChange={(e) => upd("situatie_date", e.target.value)} className="border-blue-300" /></div>
            <div><Label>Nr. contract</Label><Input value={form.contract_number} onChange={(e) => upd("contract_number", e.target.value)} /></div>
            <div><Label>TVA %</Label><Input type="number" value={form.tva_percent} onChange={(e) => upd("tva_percent", parseFloat(e.target.value) || 21)} /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Obiect lucrare</Label><Input value={form.obiect_lucrare} onChange={(e) => upd("obiect_lucrare", e.target.value)} /></div>
            <div><Label>Locație</Label><Input value={form.locatie_lucrare} onChange={(e) => upd("locatie_lucrare", e.target.value)} /></div>
          </div>

          {/* Info banner despre logica cantităților */}
          {devizData?.workItems && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2" data-testid="situatie-info-banner">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
              <div>
                {previousQtyMap && Object.keys(previousQtyMap).length > 0 ? (
                  <>
                    Cantitățile de mai jos sunt cele <b>rămase de facturat</b> (deviz − situații anterioare).
                    Prețul unitar include contribuția + indirectele + profitul din deviz, astfel încât <b>suma totală a tuturor Situațiilor de Lucrări = valoarea contractului</b>.
                  </>
                ) : (
                  <>
                    Prima Situație de Lucrări: valorile sunt setate la <b>total deviz</b>.
                    Ajustează cantitățile dacă emiți doar o parte — restul va apărea automat la următoarea SL.
                  </>
                )}
                {markupFactor > 1 && (
                  <span className="ml-1 opacity-80">
                    (factor markup aplicat: <b>×{markupFactor.toFixed(4)}</b>)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Tabel articole - stil albastru */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="situatie-items-table">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-2 py-2 text-left w-8">Nr</th>
                  <th className="px-2 py-2 text-left">Descriere lucrare</th>
                  <th className="px-2 py-2 text-center w-16">UM</th>
                  <th className="px-2 py-2 text-right w-24">Cantitate</th>
                  <th className="px-2 py-2 text-right w-28">Preț unitar</th>
                  <th className="px-2 py-2 text-right w-28">Valoare</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const val = (item.quantity || 0) * (item.price || 0);
                  const devizQty = parseFloat(item.deviz_quantity || 0);
                  const alreadyInv = parseFloat(item.already_invoiced || 0);
                  const maxRemaining = Math.max(0, devizQty - alreadyInv);
                  const overLimit = devizQty > 0 && (parseFloat(item.quantity || 0) > maxRemaining + 0.001);
                  return (
                    <tr key={item.id || `sit-${idx}`} className={idx % 2 === 0 ? "bg-blue-50/50" : "bg-white"}>
                      <td className="px-2 py-1 text-center text-slate-500">{idx + 1}</td>
                      <td className="px-1 py-1">
                        <Input value={item.name} onChange={(e) => updateItem(idx, "name", e.target.value)} className="h-8 text-sm border-blue-200" />
                      </td>
                      <td className="px-1 py-1">
                        <Input value={item.um} onChange={(e) => updateItem(idx, "um", e.target.value)} className="h-8 text-sm text-center border-blue-200 w-14" />
                      </td>
                      <td className="px-1 py-1">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number" step="0.01" min="0"
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                            className={`h-8 text-sm text-right border-blue-200 ${overLimit ? "border-red-400 bg-red-50" : ""}`}
                          />
                          {devizQty > 0 && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => updateItem(idx, "quantity", maxRemaining)}
                              className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-100"
                              title={`Setează la cantitatea rămasă: ${maxRemaining.toFixed(2)}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                        {devizQty > 0 && (
                          <div className={`text-[10px] mt-0.5 ${overLimit ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                            {alreadyInv > 0
                              ? `Deviz: ${devizQty.toFixed(2)} · Facturat: ${alreadyInv.toFixed(2)} · Rămas: ${maxRemaining.toFixed(2)}`
                              : `Deviz: ${devizQty.toFixed(2)}`}
                            {overLimit && ` ⚠ depășește!`}
                          </div>
                        )}
                      </td>
                      <td className="px-1 py-1">
                        <Input type="number" step="0.01" value={item.price} onChange={(e) => updateItem(idx, "price", parseFloat(e.target.value) || 0)} className="h-8 text-sm text-right border-blue-200" />
                      </td>
                      <td className="px-2 py-1 text-right font-semibold text-blue-700">{val.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-1 py-1">
                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Button variant="outline" size="sm" onClick={addItem} className="border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="add-situatie-item">
            <Plus className="w-4 h-4 mr-1" /> Adaugă articol
          </Button>

          {/* Totaluri */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between p-2 bg-blue-50 rounded">
                <span className="text-blue-700 font-medium">Total fără TVA:</span>
                <span className="font-bold">{totals.totalFaraTVA.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei</span>
              </div>
              <div className="flex justify-between p-2 bg-blue-50 rounded">
                <span className="text-blue-700 font-medium">TVA ({form.tva_percent}%):</span>
                <span className="font-bold">{totals.tva.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei</span>
              </div>
              <div className="flex justify-between p-2 bg-blue-600 text-white rounded">
                <span className="font-medium">TOTAL CU TVA:</span>
                <span className="font-bold">{totals.totalCuTVA.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>Anulează</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-situatie-lucrari">
              <Save className="w-4 h-4 mr-2" />Salvează Situația de Lucrări
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
